import { Fragment, type ReactNode } from "react";
import styles from "./ListingSummary.module.css";

export type ListingCount = {
  label: string;
  value: number;
  // Present when a filter is in force, so the figure reads "3 of 28" and the
  // filter's effect is legible. A count with no filter behind it — "3 archived"
  // — leaves it out and reads as a plain number.
  total?: number;
  // Nouns take a plural s; adjectives standing in for one ("archived",
  // "inactive") do not.
  shouldPluralize?: boolean;
};

const Count = ({
  label,
  value,
  total,
  shouldPluralize = true,
}: ListingCount) => (
  <span>
    <span className={styles.number}>
      {total === undefined || value === total ? value : `${value} of ${total}`}
    </span>{" "}
    {label}
    {shouldPluralize && (total ?? value) !== 1 ? "s" : ""}
  </span>
);

type ListingSummaryProps = {
  // Says what is on screen, not what is in the database — a count that ignored
  // the filter would contradict the rows underneath it.
  counts: ListingCount[];
  filters?: ReactNode;
};

export const ListingSummary = ({ counts, filters }: ListingSummaryProps) => (
  <div className={styles.summary}>
    <div className={styles.counts}>
      {counts.map((count, index) => (
        <Fragment key={count.label}>
          {index === 0 ? null : <span className={styles.separator}>·</span>}
          <Count {...count} />
        </Fragment>
      ))}
    </div>
    {filters === undefined ? null : (
      <div className={styles.filters}>{filters}</div>
    )}
  </div>
);
